import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Cable,
  LayoutDashboard,
  LayoutGrid,
  Map,
  MonitorDown,
  Plus,
  Route,
  Settings,
  UserCog,
  UsersRound,
  Video,
} from 'lucide-react'
import { useProductEvent } from '@/hooks/useProductEvent'
import { useFirstVideoEvents } from '@/hooks/useFirstVideoEvents'
import { SiteFooter } from '@/components/SiteFooter'
import { ContentSkeleton, Skeleton } from '@/components/loading/Skeleton'
import {
  EventLiveKitProvider,
  LiveKitConnectionStatus,
  useEventLiveKit,
  useEventLiveKitLivePublisherIdentities,
} from '@/components/livekit/EventLiveKitProvider'
import {
  BackLink,
  Button,
  Drawer,
  EmptyState,
  IconButton,
  IconButtonLink,
  Notice,
  Tooltip,
} from '@/components/play'
import { EventCourseForm } from '@/components/EventCourseForm'
import { EventAthletesPanel } from '@/components/athletes/EventAthletesPanel'
import { OBSSetupPanel } from '@/components/obs/OBSSetupPanel'
import { CameraInvitePanel } from '@/components/workspace/CameraInvitePanel'
import { EventHosts } from '@/components/workspace/EventHosts'
import { EventTeam } from '@/components/workspace/EventTeam'
import { LiveShowPanel } from '@/components/workspace/LiveShowPanel'
import { SrtAddressesPanel } from '@/components/workspace/SrtAddressesPanel'
import { ShareEvent } from '@/components/workspace/ShareEvent'
import { WorkspaceHeader } from '@/components/workspace/WorkspaceHeader'
import {
  WorkspaceMultiview,
  type MultiviewLayout,
} from '@/components/workspace/WorkspaceMultiview'
import {
  WorkspaceCamera,
  WorkspacePreview,
} from '@/components/workspace/WorkspaceCamera'
import {
  WorkspaceMap,
  type WorkspaceMapView,
} from '@/components/workspace/WorkspaceMap'
import { useEventWorkspace } from '@/components/workspace/useEventWorkspace'
import { useWorkspaceExit, type WorkspaceExit } from '@/components/workspace/useWorkspaceExit'
import { useStreamTelemetry } from '@/hooks/useStreamTelemetry'
import { cameraLocations, isCamera, workspaceError } from '@/lib/workspace'
import { publicEventUrl } from '@/lib/eventLinks'
import { api } from '@/util/api'
import type { StreamResource } from '@/types/events'
import '@/components/workspace/workspace.css'
import '@/components/workspace/view-switcher.css'

type WorkspaceMode = 'dashboard' | 'multiview' | 'map' | 'athletes' | 'team'

/** The workspace's views, in the switcher's order: each its mode, path under the event, name and icon. */
const WORKSPACE_VIEWS: { mode: WorkspaceMode; path: string; label: string; icon: LucideIcon }[] = [
  { mode: 'dashboard', path: '', label: 'Overview', icon: LayoutDashboard },
  { mode: 'multiview', path: '/multiview', label: 'Multiview', icon: LayoutGrid },
  { mode: 'map', path: '/map', label: 'Map', icon: Map },
  { mode: 'athletes', path: '/athletes', label: 'Athletes', icon: UsersRound },
  { mode: 'team', path: '/team', label: 'Team', icon: UserCog },
]

/**
 * One view in the switcher: its icon and name. On a phone the five don't fit by name, so only the current view shows
 * its name (view-switcher.css); the others show their icon, keep the name as their accessible name, and name
 * themselves in a tooltip, which only phones show.
 */
function WorkspaceViewLink({
  view,
  eventId,
  current,
}: {
  view: (typeof WORKSPACE_VIEWS)[number]
  eventId: string
  current: boolean
}) {
  const Icon = view.icon
  const link = (
    <NavLink end to={`/events/${eventId}${view.path}`}>
      <Icon aria-hidden="true" />
      <span className="pw-view-label">{view.label}</span>
    </NavLink>
  )
  return current ? link : <Tooltip content={view.label} side="bottom" className="pw-view-tooltip">{link}</Tooltip>
}

export function EventPage() {
  const { id } = useParams<{ id: string }>()
  return id ? <EventWorkspace key={id} eventId={id} /> : null
}

function EventWorkspace({ eventId }: { eventId: string }) {
  // A manager who is removed (or leaves) goes to their events, once, however it is noticed.
  const exit = useWorkspaceExit()
  const workspace = useEventWorkspace(eventId, { onAccessLost: exit.lost })
  return (
    <EventLiveKitProvider config={workspace.config} renderAudio={false}>
      <EventWorkspaceContent eventId={eventId} workspace={workspace} exit={exit} />
    </EventLiveKitProvider>
  )
}

function EventWorkspaceContent({
  eventId,
  workspace,
  exit,
}: {
  eventId: string
  workspace: ReturnType<typeof useEventWorkspace>
  exit: WorkspaceExit
}) {
  const {
    event,
    streams,
    loading,
    error,
    load,
    mergeStream,
    removeStream,
    setEvent,
    setPreviewConnected,
  } = workspace
  const { connectionState, enabled: previewEnabled } = useEventLiveKit()
  useEffect(
    () => setPreviewConnected(connectionState === 'connected'),
    [connectionState, setPreviewConnected],
  )
  const location = useLocation()
  const path = location.pathname
  const handoffHandled = useRef('')
  const mode: WorkspaceMode = path.endsWith('/map')
    ? 'map'
    : path.endsWith('/multiview')
      ? 'multiview'
      : path.endsWith('/athletes')
        ? 'athletes'
        : path.endsWith('/team')
          ? 'team'
        : 'dashboard'
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteId, setInviteId] = useState<string | null>(null)
  const [obsOpen, setObsOpen] = useState(false)
  const [srtOpen, setSrtOpen] = useState(false)
  const [courseOpen, setCourseOpen] = useState(false)
  const [courseVersion, setCourseVersion] = useState(0)
  const [view, setView] = useState<WorkspaceMapView | null>(null)
  const [multiviewLayout, setMultiviewLayout] = useState<MultiviewLayout>('auto')
  const [liveShowBusy, setLiveShowBusy] = useState(false)
  const programmeRequest = useRef<Promise<StreamResource> | null>(null)
  const [featureBusy, setFeatureBusy] = useState(false)
  const [actionError, setActionError] = useState('')
  const track = useProductEvent(eventId)
  const identities = useEventLiveKitLivePublisherIdentities()
  useFirstVideoEvents(eventId, streams, identities)
  const telemetry = useStreamTelemetry(eventId, { staleThresholdMs: 86400000 })
  const cameras = useMemo(() => streams.filter(isCamera), [streams])
  const programme = streams.find((stream) => !isCamera(stream))
  const locations = useMemo(
    () => cameraLocations(cameras, telemetry),
    [cameras, telemetry],
  )
  const preview = streams.find((stream) => stream.id === previewId) || null
  const invite = cameras.find((stream) => stream.id === inviteId) || null
  const liveCount = cameras.filter((stream) =>
    identities.has(stream.attributes.participant_identity || ''),
  ).length
  const liveLabel = `${liveCount} of ${cameras.length} ${cameras.length === 1 ? 'camera' : 'cameras'} live`
  const featured = event?.attributes.featured_participant_identity
  const publicUrl = publicEventUrl(event ?? { id: eventId })
  const publicEnabled = event?.attributes.template_key !== 'none'
  function showPreview(stream: StreamResource) {
    setSelectedId(stream.id)
    setPreviewId(stream.id)
  }
  function showInvitation(stream?: StreamResource) {
    setPreviewId(null)
    setInviteId(stream?.id || null)
    setSelectedId(stream?.id || null)
    setInviteOpen(true)
  }
  function enableLiveShow(): Promise<StreamResource> {
    if (programme) return Promise.resolve(programme)
    if (programmeRequest.current) return programmeRequest.current
    setLiveShowBusy(true)
    programmeRequest.current = api
      .post<{ data: StreamResource }>(`/events/${eventId}/programme`)
      .then(({ data }) => {
        mergeStream(data)
        return data
      })
      .finally(() => {
        programmeRequest.current = null
        setLiveShowBusy(false)
      })
    return programmeRequest.current
  }
  function showObsSetup() {
    setPreviewId(null)
    setObsOpen(true)
  }
  useEffect(() => {
    if (loading || !event || handoffHandled.current === location.search) return
    handoffHandled.current = location.search
    const query = new URLSearchParams(location.search)
    if (query.get('panel') === 'obs') setObsOpen(true)
    const cameraId = query.get('camera')
    if (cameraId) {
      const camera = cameras.find((item) => item.id === cameraId)
      if (camera) {
        setInviteId(camera.id)
        setSelectedId(camera.id)
        setInviteOpen(true)
      } else {
        setActionError('This camera invitation is unavailable. Choose a camera from your workspace.')
      }
    }
    if (query.get('entry_surface') === 'chatgpt') track('chatgpt_workspace_opened')
  }, [loading, event, cameras, location.search, track])
  async function changeVisibility(stream: StreamResource, publiclyVisible: boolean) {
    const { data } = await api.patch<{ data: StreamResource }>(
      `/streams/${stream.id}`,
      { stream: { publicly_visible: publiclyVisible } },
    )
    mergeStream(data)
  }
  function videoActions(stream: StreamResource) {
    const identity = stream.attributes.participant_identity
    const isFeatured = !!identity && featured === identity
    const cameraPublic = stream.attributes.publicly_visible !== false
    const hasVideo = !!identity && identities.has(identity)
    return {
      featured: isFeatured,
      onVisibilityChange: (visible: boolean) => changeVisibility(stream, visible),
      onFeature: () => feature(stream),
      featureBusy,
      canFeature: isFeatured || (!!identity && publicEnabled && cameraPublic &&
        (isCamera(stream) || hasVideo)),
      featureDisabledReason: !publicEnabled
        ? 'Turn on the public event page in event settings first'
        : !cameraPublic
          ? 'Make this video public before featuring it'
          : 'Wait for video before featuring this show',
    }
  }
  async function feature(stream: StreamResource) {
    if (featureBusy || !stream.attributes.participant_identity) return
    setFeatureBusy(true)
    setActionError('')
    const identity =
      featured === stream.attributes.participant_identity
        ? null
        : stream.attributes.participant_identity
    try {
      await api.post(`/events/${eventId}/live/featured_camera`, {
        participant_identity: identity,
      })
      setEvent(
        (previous) =>
          previous && {
            ...previous,
            attributes: {
              ...previous.attributes,
              featured_participant_identity: identity,
            },
          },
      )
    } catch (failure) {
      setActionError(
        workspaceError(
          failure,
          'The featured view couldn’t be changed. Try again.',
        ),
      )
      throw failure
    } finally {
      setFeatureBusy(false)
    }
  }
  return (
    <div className={`play-theme pw-workspace pw-workspace--${mode}`}>
      <WorkspaceHeader label="Host workspace" />
      <main className="pw-content">
        <BackLink to="/app" className="pw-back">Events</BackLink>
        {loading && !event ? (
          <div className="space-y-8 py-8">
            <Skeleton className="h-12 w-72 max-w-full" />
            <ContentSkeleton label="Getting your event ready" variant={mode === 'map' ? 'media' : 'cards'} />
          </div>
        ) : error && !event ? (
          <>
            <h1 className="pw-load-title">Your event</h1>
            <Notice
              tone="error"
              alert
              title="Your event couldn’t load"
              action={<Button onClick={() => load()}>Try again</Button>}
            >
              {error}
            </Notice>
          </>
        ) : (
          event && (
            <>
              <div className="pw-event-heading">
                <div className="pw-event-title">
                  <h1>{event.attributes.name}</h1>
                  <div className="pw-event-meta">
                    <Tooltip content={liveLabel}>
                      <span
                        role="img"
                        tabIndex={0}
                        aria-label={liveLabel}
                        className={
                          liveCount
                            ? 'pw-live-count pw-live-count--live'
                            : 'pw-live-count'
                        }
                      >
                        <span className="pw-live-count__icon">
                          <Video aria-hidden="true" />
                        </span>
                        {liveCount}/{cameras.length}
                      </span>
                    </Tooltip>
                    {/* Connected previews need no badge; connecting, offline or unavailable does. */}
                    {(!previewEnabled || connectionState !== 'connected') && (
                      <LiveKitConnectionStatus />
                    )}
                  </div>
                </div>
                <div className="pw-event-actions">
                  <div className="pw-event-tools">
                    <IconButton
                      label="OBS setup"
                      variant="ghost"
                      onClick={() => setObsOpen(true)}
                    >
                      <MonitorDown />
                    </IconButton>
                    <IconButton
                      label="SRT addresses"
                      variant="ghost"
                      onClick={() => setSrtOpen(true)}
                    >
                      <Cable />
                    </IconButton>
                    <IconButtonLink to={`/events/${eventId}/settings`} variant="ghost" label="Event settings">
                      <Settings />
                    </IconButtonLink>
                  </div>
                  <ShareEvent
                    url={publicUrl}
                    enabled={publicEnabled}
                    settingsTo={`/events/${eventId}/settings`}
                    onCopy={(copiedLink) => {
                      if (copiedLink) track('public_event_link_copied')
                    }}
                  />
                  {(cameras.length > 0 || programme) && (
                    <Button onClick={() => showInvitation()}>
                      <Plus />
                      Add camera
                    </Button>
                  )}
                </div>
              </div>
              <div className="pw-viewbar">
                <nav aria-label="Event views" className="pw-view-switcher pw-view-switcher--fill">
                  {WORKSPACE_VIEWS.map((view) => (
                    <WorkspaceViewLink key={view.mode} view={view} eventId={eventId} current={view.mode === mode} />
                  ))}
                </nav>
                <div className="pw-view-options">
                  {mode !== 'map' && mode !== 'team' && (
                    <IconButton
                      label="Event course"
                      variant="ghost"
                      onClick={() => setCourseOpen(true)}
                    >
                      <Route />
                    </IconButton>
                  )}
                </div>
              </div>
              {actionError && (
                <Notice tone="error" alert>{actionError}</Notice>
              )}
              {previewEnabled && connectionState === 'disconnected' && mode !== 'team' && (
                <Notice
                  tone="warning"
                  className="mb-l"
                  action={<Button variant="secondary" size="compact" loading={loading} onClick={() => load(true, true)}>Reconnect previews</Button>}
                >
                  Live previews are disconnected.
                </Notice>
              )}
              {mode === 'map' ? (
                <WorkspaceMap
                  eventId={eventId}
                  cameras={cameras}
                  locations={locations}
                  selectedId={selectedId}
                  featuredIdentity={featured}
                  onPreview={showPreview}
                  onDetails={showInvitation}
                  view={view}
                  onViewChange={setView}
                  courseVersion={courseVersion}
                  onCourse={() => setCourseOpen(true)}
                />
              ) : mode === 'athletes' ? (
                <EventAthletesPanel eventId={eventId} />
              ) : mode === 'team' ? (
                <div className="pw-team-view">
                  <EventTeam event={event} exit={exit} />
                </div>
              ) : mode === 'multiview' ? (
                <WorkspaceMultiview
                  cameras={cameras}
                  programme={programme}
                  layout={multiviewLayout}
                  onLayoutChange={setMultiviewLayout}
                  selectedId={selectedId}
                  featuredIdentity={featured}
                  onSelect={setSelectedId}
                  onPreview={showPreview}
                  onAddCamera={() => showInvitation()}
                  onVisibilityChange={changeVisibility}
                  onDetails={showInvitation}
                  onFeature={feature}
                  featureBusy={featureBusy}
                  publicEnabled={publicEnabled}
                  onSetup={showObsSetup}
                />
              ) : (
                <div className="pw-overview-layout">
                  <div className="pw-overview-cameras">
                    {!cameras.length ? (
                      <EmptyState
                        icon={<Video />}
                        title="Bring your first camera along."
                        description="Name a view and share its invitation. When the camera joins, you’ll see it here."
                        action={
                          <Button onClick={() => showInvitation()}>
                            Add your first camera
                            <Plus />
                          </Button>
                        }
                      />
                    ) : (
                      <>
                        {mode === 'dashboard' && (
                          <div className="pw-section-heading">
                            <h2>
                              Your cameras<span>{cameras.length}</span>
                            </h2>
                          </div>
                        )}
                        <div className="pw-camera-grid pw-camera-grid--dashboard">
                          {cameras.map((stream, index) => (
                            <WorkspaceCamera
                              key={stream.id}
                              stream={stream}
                              index={index}
                              selected={selectedId === stream.id}
                              {...videoActions(stream)}
                              onPreview={() => showPreview(stream)}
                              onDetails={() => showInvitation(stream)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <LiveShowPanel
                    stream={programme}
                    enabling={liveShowBusy}
                    featured={!!featured && featured === programme?.attributes.participant_identity}
                    featureBusy={featureBusy}
                    publicEnabled={publicEnabled}
                    onEnable={enableLiveShow}
                    onPreview={() => programme && showPreview(programme)}
                    onFeature={() => programme ? feature(programme) : Promise.resolve()}
                    onSetup={showObsSetup}
                    onVisibilityChange={programme
                      ? (visible) => changeVisibility(programme, visible)
                      : undefined}
                  />
                  <EventHosts event={event} exit={exit} />
                </div>
              )}
              <CameraInvitePanel
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                eventId={eventId}
                eventName={event.attributes.name}
                stream={invite}
                onSaved={(stream) => {
                  const created = !cameras.some(
                    (camera) => camera.id === stream.id,
                  )
                  mergeStream(stream)
                  setInviteId(stream.id)
                  setSelectedId(stream.id)
                  if (created) track('camera_created', { camera_id: stream.id })
                }}
                onDeleted={(id) => {
                  removeStream(id)
                  if (selectedId === id) setSelectedId(null)
                  if (previewId === id) setPreviewId(null)
                }}
              />
              <OBSSetupPanel
                eventId={eventId}
                eventName={event.attributes.name}
                streams={streams}
                onEnableLiveShow={enableLiveShow}
                liveShowBusy={liveShowBusy}
                open={obsOpen}
                onOpenChange={setObsOpen}
              />
              <SrtAddressesPanel
                eventName={event.attributes.name}
                cameras={cameras}
                programme={programme}
                onEnableLiveShow={enableLiveShow}
                liveShowBusy={liveShowBusy}
                open={srtOpen}
                onOpenChange={setSrtOpen}
              />
              <WorkspacePreview
                stream={preview}
                {...(preview ? videoActions(preview) : {})}
                onDetails={preview && isCamera(preview) ? () => showInvitation(preview) : undefined}
                onSetup={preview && !isCamera(preview) ? showObsSetup : undefined}
                info={preview && !isCamera(preview)
                  ? 'Your finished mix from OBS. Open OBS setup for the destination and import steps.'
                  : undefined}
                onClose={() => setPreviewId(null)}
              />
              <Drawer
                open={courseOpen}
                onOpenChange={(open) => {
                  setCourseOpen(open)
                  if (!open) setCourseVersion((value) => value + 1)
                }}
                title="Bring the course along."
                description="Upload a GPX file or import from LiveLabs Maps to show the route on your map."
                className="pw-panel"
              >
                <div className="pw-form">
                  <EventCourseForm eventId={eventId} />
                </div>
              </Drawer>
            </>
          )
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
